// Implements: .claude/skills/rehaut-design/ui_kits/rehaut-pacientes/primitives.jsx (useCountUp)
// See: .claude/skills/rehaut-design/HANDOFF.md
'use client';

import { useEffect, useState } from 'react';

/**
 * Number tick-up hook. Eases from 0 to `target` over `durationMs` after
 * an optional `delay`. ease-out cubic so the count settles softly at
 * the end rather than slamming. Returns the integer value at each
 * frame so callers can render directly without rounding.
 *
 * Honors prefers-reduced-motion: if the user has set it, we jump
 * straight to the target value on mount instead of animating.
 */
export function useCountUp(target: number, durationMs = 700, delay = 0): number {
  const [v, setV] = useState(0);

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setV(target);
      return;
    }

    let raf = 0;
    const start = performance.now() + delay;
    const tick = (now: number) => {
      const t = Math.max(0, Math.min(1, (now - start) / durationMs));
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setV(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, delay]);

  return v;
}
